import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';

@Component({
  selector: 'app-message-seen-info-modal',
  standalone: true,
  imports: [CommonModule, DatePipe],
  styleUrls: ['./message-seen-info-modal.component.css'],
  template: `
    <div class="seen-info-modal-overlay" [class.dark-theme]="isDarkMode" (click)="close.emit()">
      <div class="seen-info-modal" [class.dark-theme]="isDarkMode" (click)="$event.stopPropagation()">

        <!-- Header -->
        <div class="seen-info-modal-header">
          <h5 class="seen-info-modal-title">Message seen by</h5>
          <button class="seen-info-close-btn" (click)="close.emit()">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
              viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="seen-info-modal-body">
          @if (seenMembers.length === 0) {
            <div class="seen-info-empty-state">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48"
                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
              <p class="seen-info-empty-text">No one has seen this message yet</p>
            </div>
          } @else {
            <div class="seen-info-list">
              @for (member of seenMembers; track member.userId) {
                <div class="seen-info-item">
                  <!-- Avatar -->
                  <div class="seen-info-avatar-wrapper">
                    @if (member.profileImage) {
                      <img
                        [src]="member.profileImage"
                        class="seen-info-avatar"
                        (error)="handleImageError($event)"
                        [alt]="member.displayName"
                      />
                    } @else {
                      <div class="seen-info-avatar-placeholder">
                        {{ getInitials(member.displayName) }}
                      </div>
                    }
                  </div>

                  <!-- Member Details -->
                  <div class="seen-info-details">
                    <span class="seen-info-name">{{ member.displayName }}</span>
                    <small class="seen-info-time">
                      @if (member.seenAt) {
                        Seen &bull; {{ member.seenAt | date:'MMM d, y, h:mm a' }}
                      } @else {
                        Seen
                      }
                    </small>
                  </div>

                  <!-- Seen Icon -->
                  <div class="seen-info-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                      viewBox="0 0 24 24" fill="none" stroke="#4CAF50"
                      stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                </div>
              }
            </div>
          }
        </div>

        <!-- Footer with stats -->
        @if (seenMembers.length > 0) {
          <div class="seen-info-modal-footer">
            <span class="seen-info-stats">
              {{ seenMembers.length }} of {{ totalMembers }} members have seen this message
            </span>
          </div>
        }

      </div>
    </div>
  `
})
export class MessageSeenInfoModalComponent {
  @Input() visible = false;
  @Input() seenMembers: SeenMember[] = [];
  @Input() totalMembers: number = 0;
  @Output() close = new EventEmitter<void>();

  get isDarkMode(): boolean {
    return typeof document !== 'undefined' && (
      document.body.classList.contains('dark-theme') ||
      document.documentElement.classList.contains('dark-theme')
    );
  }

  handleImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    const placeholder = img.nextElementSibling as HTMLElement;
    if (placeholder) {
      placeholder.style.display = 'flex';
    }
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
}

export interface SeenMember {
  userId: string;
  displayName: string;
  profileImage?: string;
  seenAt?: Date | string;
}
